import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ImageDropZone from './ImageDropZone';
import ImageWithFallback from './ImageWithFallback';
import Api from '../../api';
import {GalleryImage, StoredImage} from '../../state/types/Company';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

const MAX_IMAGES = 20;

interface BilderTabProps {
    companyId: string;
    gallery: GalleryImage[];
    setGallery: (v: GalleryImage[]) => void;
    readOnly?: boolean;
}

const BilderTab: React.FC<BilderTabProps> = ({
    companyId,
    gallery,
    setGallery,
    readOnly = false,
}) => {
    const [allImages, setAllImages] = useState<StoredImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<StoredImage | null>(null);
    const [deleting, setDeleting] = useState(false);

    const galleryPaths = gallery.map(g => g.src);

    const loadImages = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const images = await Api.Image.list(companyId);
            setAllImages(images);
        } catch {
            setLoadError('Bilder konnten nicht geladen werden. Bitte Seite neu laden.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        // Editors have read-only access; the image-list API is admin-only, so skip it
        // and render the public gallery directly from the company's saved gallery data.
        if (!readOnly) {
            loadImages();
        }
    }, [loadImages, readOnly]);

    const nonLogoImages = allImages.filter(
        img => !img.path.includes('/logo/'),
    );

    const handleUploadGallery = useCallback(async (files: File[]) => {
        setUploading(true);
        setUploadError(null);
        let succeeded = 0;
        let failed = 0;
        try {
            for (const file of files) {
                if (nonLogoImages.length + succeeded >= MAX_IMAGES) break;
                try {
                    await Api.Image.upload(companyId, 'gallery', file);
                    succeeded++;
                } catch {
                    failed++;
                }
            }
            if (failed > 0) {
                setUploadError(
                    `${failed} Bild(er) konnten nicht hochgeladen werden.`,
                );
            }
        } catch {
            setUploadError('Beim Hochladen ist ein Fehler aufgetreten.');
        } finally {
            setUploading(false);
            await loadImages();
        }
    }, [companyId, nonLogoImages.length, loadImages]);

    const handleDeleteConfirmed = useCallback(async () => {
        if (!deleteConfirm) return;
        setDeleting(true);
        try {
            await Api.Image.delete(deleteConfirm.path);
            // Remove from gallery if it was public
            setGallery(
                gallery.filter(g => g.src !== deleteConfirm.url),
            );
            await loadImages();
        } catch {
            setUploadError('Bild konnte nicht gelöscht werden.');
        } finally {
            setDeleting(false);
            setDeleteConfirm(null);
        }
    }, [deleteConfirm, gallery, setGallery, loadImages]);

    const togglePublic = useCallback(
        (imageUrl: string) => {
            if (galleryPaths.includes(imageUrl)) {
                setGallery(gallery.filter(g => g.src !== imageUrl));
            } else {
                setGallery([
                    ...gallery,
                    {src: imageUrl, order: gallery.length},
                ]);
            }
        },
        [gallery, galleryPaths, setGallery],
    );

    const togglePrimary = useCallback(
        (imageUrl: string) => {
            setGallery(
                gallery.map(g => ({
                    ...g,
                    primary: g.src === imageUrl ? !g.primary : false,
                })),
            );
        },
        [gallery, setGallery],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 5},
        }),
    );

    const onDragEnd = useCallback(
        (event: DragEndEvent) => {
            const {active, over} = event;
            if (!over || active.id === over.id) return;

            const sortedGallery = [...gallery]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const oldIndex = sortedGallery.findIndex(
                g => g.src === active.id,
            );
            const newIndex = sortedGallery.findIndex(
                g => g.src === over.id,
            );
            if (oldIndex === -1 || newIndex === -1) return;

            const reordered = arrayMove(sortedGallery, oldIndex, newIndex);
            setGallery(
                reordered.map((g, i) => ({...g, order: i})),
            );
        },
        [gallery, setGallery],
    );

    // Build display list: public images sorted by order first,
    // then non-public images
    const publicImages = nonLogoImages
        .filter(img => galleryPaths.includes(img.url))
        .sort((a, b) => {
            const orderA = gallery.find(
                g => g.src === a.url,
            )?.order ?? 0;
            const orderB = gallery.find(
                g => g.src === b.url,
            )?.order ?? 0;
            return orderA - orderB;
        });
    const privateImages = nonLogoImages.filter(
        img => !galleryPaths.includes(img.url),
    );

    if (readOnly) {
        const sortedGallery = [...gallery]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return (
            <Box>
                <Card sx={{marginBottom: 2}}>
                    <CardContent>
                        <Typography variant={'body1'} sx={{fontWeight: 'bold', mb: 2}}>
                            Öffentliche Galerie ({sortedGallery.length} Bilder)
                        </Typography>
                        {sortedGallery.length === 0 ? (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                Keine öffentlichen Bilder vorhanden.
                            </Typography>
                        ) : (
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
                                {sortedGallery.map((g, idx) => (
                                    <Box
                                        key={g.src}
                                        sx={{
                                            width: 160,
                                            border: '2px solid',
                                            borderColor: g.primary ? 'warning.main' : 'grey.300',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            backgroundColor: 'background.paper',
                                        }}
                                    >
                                        <ImageWithFallback
                                            src={g.src}
                                            sx={{
                                                width: '100%',
                                                height: 120,
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 0.5,
                                            py: 0.25,
                                            backgroundColor: 'grey.50',
                                        }}
                                        >
                                            <Typography variant={'caption'} sx={{fontWeight: 'bold'}}>
                                                #{idx + 1}
                                            </Typography>
                                            {g.primary && (
                                                <StarIcon fontSize={'small'} color={'warning'} />
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: 4,
            }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {loadError && (
                <Alert severity={'error'} sx={{marginBottom: 2}} onClose={() => setLoadError(null)}>
                    {loadError}
                </Alert>
            )}
            {/* Upload Section */}
            <Card sx={{marginBottom: 2}}>
                <CardContent>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                    }}
                    >
                        <Typography
                            variant={'body1'}
                            sx={{fontWeight: 'bold'}}
                        >
                            Galerie
                        </Typography>
                        <Typography
                            variant={'body2'}
                            color={'text.secondary'}
                        >
                            {nonLogoImages.length} / {MAX_IMAGES} Bilder
                            {' | '}{gallery.length} öffentlich
                        </Typography>
                    </Box>
                    <Typography
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{display: 'block', mb: 2}}
                    >
                        Upload und Löschen werden sofort gespeichert.
                        Sichtbarkeit und Reihenfolge erst beim Speichern.
                    </Typography>
                    <ImageDropZone
                        onFilesSelected={handleUploadGallery}
                        disabled={
                            uploading
                            || nonLogoImages.length >= MAX_IMAGES
                        }
                        label={
                            nonLogoImages.length >= MAX_IMAGES
                                ? `Maximum von ${MAX_IMAGES} Bildern erreicht`
                                : 'Bilder hierher ziehen oder klicken'
                        }
                    />
                    {uploading && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 1,
                        }}
                        >
                            <CircularProgress size={16} />
                            <Typography variant={'caption'}>
                                Wird hochgeladen...
                            </Typography>
                        </Box>
                    )}
                    {uploadError && (
                        <Alert
                            severity={'error'}
                            sx={{marginTop: 1}}
                            onClose={() => setUploadError(null)}
                        >
                            {uploadError}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Public images with DnD */}
            {publicImages.length > 0 && (
                <Card sx={{marginBottom: 2}}>
                    <CardContent>
                        <Typography
                            variant={'body1'}
                            sx={{fontWeight: 'bold', mb: 1}}
                        >
                            Öffentliche Galerie
                            ({publicImages.length} Bilder)
                        </Typography>
                        <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{display: 'block', mb: 2}}
                        >
                            Reihenfolge per Drag & Drop ändern
                        </Typography>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={onDragEnd}
                        >
                            <SortableContext
                                items={publicImages.map(img => img.url)}
                                strategy={rectSortingStrategy}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                                >
                                    {publicImages.map((img, idx) => {
                                        const gItem = gallery.find(
                                            g => g.src === img.url,
                                        );
                                        return (
                                            <SortableImageCard
                                                key={img.url}
                                                id={img.url}
                                                img={img}
                                                isPublic={true}
                                                isPrimary={gItem?.primary || false}
                                                order={idx + 1}
                                                onTogglePublic={() => togglePublic(img.url)}
                                                onTogglePrimary={() => togglePrimary(img.url)}
                                                onDelete={() => setDeleteConfirm(img)}
                                            />
                                        );
                                    })}
                                </Box>
                            </SortableContext>
                        </DndContext>
                    </CardContent>
                </Card>
            )}

            {/* Non-public images */}
            {privateImages.length > 0 && (
                <Card sx={{marginBottom: 2}}>
                    <CardContent>
                        <Typography
                            variant={'body1'}
                            sx={{fontWeight: 'bold', mb: 2}}
                        >
                            Nicht öffentlich
                            ({privateImages.length} Bilder)
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                        >
                            {privateImages.map(img => (
                                <ImageCard
                                    key={img.path}
                                    img={img}
                                    isPublic={false}
                                    isPrimary={false}
                                    onTogglePublic={
                                        () => togglePublic(img.url)
                                    }
                                    onTogglePrimary={
                                        () => togglePrimary(img.url)
                                    }
                                    onDelete={
                                        () => setDeleteConfirm(img)
                                    }
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {nonLogoImages.length === 0 && (
                <Card sx={{marginBottom: 2}}>
                    <CardContent>
                        <Typography
                            variant={'body2'}
                            color={'text.secondary'}
                        >
                            Noch keine Bilder hochgeladen.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {gallery.length < 5 && (
                <Alert severity={'info'} sx={{marginTop: 2}}>
                    Mindestens 5 Bilder müssen als öffentlich markiert
                    werden, damit der Betrieb veröffentlicht werden kann.
                    (Aktuell: {gallery.length} / 5)
                </Alert>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
            >
                <DialogTitle>Bild löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Dieses Bild wird unwiderruflich gelöscht.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteConfirm(null)}
                        disabled={deleting}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleDeleteConfirmed}
                        color={'error'}
                        variant={'contained'}
                        disabled={deleting}
                    >
                        {deleting ? 'Wird gelöscht...' : 'Löschen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

interface ImageCardProps {
    img: StoredImage;
    isPublic: boolean;
    isPrimary: boolean;
    order?: number;
    onTogglePublic: () => void;
    onTogglePrimary: () => void;
    onDelete: () => void;
    showDragHandle?: boolean;
    style?: React.CSSProperties;
    listeners?: any;
    attributes?: any;
    setNodeRef?: (el: HTMLElement | null) => void;
}

const ImageCard: React.FC<ImageCardProps> = ({
    img,
    isPublic,
    isPrimary,
    order,
    onTogglePublic,
    onTogglePrimary,
    onDelete,
    showDragHandle,
    style,
    listeners,
    attributes,
    setNodeRef,
}) => (
    <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        sx={{
            width: 160,
            border: '2px solid',
            borderColor: isPublic ? 'primary.main' : 'grey.300',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
        }}
    >
        <ImageWithFallback
            src={img.url}
            sx={{
                width: '100%',
                height: 120,
                objectFit: 'cover',
                display: 'block',
            }}
        />
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.5,
            py: 0.25,
            backgroundColor: 'grey.50',
        }}
        >
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                {showDragHandle && (
                    <Box
                        {...listeners}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'grab',
                            mr: 0.25,
                        }}
                    >
                        <DragIndicatorIcon
                            fontSize={'small'}
                            sx={{color: 'grey.500'}}
                        />
                    </Box>
                )}
                {isPublic && order && (
                    <Typography
                        variant={'caption'}
                        sx={{fontWeight: 'bold', mr: 0.5}}
                    >
                        #{order}
                    </Typography>
                )}
                <Tooltip
                    title={isPublic
                        ? 'Aus Galerie entfernen'
                        : 'Zur Galerie hinzufügen'
                    }
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isPublic}
                                onChange={onTogglePublic}
                                size={'small'}
                                icon={
                                    <VisibilityOffIcon fontSize={'small'} />
                                }
                                checkedIcon={
                                    <VisibilityIcon fontSize={'small'} />
                                }
                            />
                        }
                        label={''}
                        sx={{margin: 0}}
                    />
                </Tooltip>
                {isPublic && (
                    <Tooltip
                        title={isPrimary
                            ? 'Primärbild entfernen'
                            : 'Als Primärbild setzen'
                        }
                    >
                        <IconButton
                            size={'small'}
                            onClick={onTogglePrimary}
                            color={'warning'}
                        >
                            {isPrimary
                                ? <StarIcon fontSize={'small'} />
                                : <StarBorderIcon fontSize={'small'} />
                            }
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Tooltip title={'Bild löschen'}>
                <IconButton
                    size={'small'}
                    color={'error'}
                    onClick={onDelete}
                >
                    <DeleteIcon fontSize={'small'} />
                </IconButton>
            </Tooltip>
        </Box>
    </Box>
);

// Sortable wrapper for public gallery images
const SortableImageCard: React.FC<
    Omit<ImageCardProps, 'showDragHandle' | 'style' | 'listeners' | 'attributes' | 'setNodeRef'>
    & {id: string}
> = ({id, ...props}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id});

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <ImageCard
            {...props}
            showDragHandle={true}
            setNodeRef={setNodeRef}
            style={style}
            listeners={listeners}
            attributes={attributes}
        />
    );
};

export default BilderTab;
